"use client";

import { useState } from "react";
import type { ProductBranding } from "@accounting-app/products";
import { useTranslations } from "next-intl";
import { Notice } from "@/components/ui/Notice";
import { trackedFetch } from "@/lib/api/trackedFetch";

export default function ForgotPasswordForm({ branding }: { branding: ProductBranding }) {
  const t = useTranslations("forgot-password");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await trackedFetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, origin: window.location.origin }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to send reset email");
      }

      setSubmitted(true);
    } catch (err) {
      console.error("[forgot-password] Error:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <a
            href="/login"
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 text-sm mb-4 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            {t("backToLogin")}
          </a>

          <div className="flex items-center gap-3 mb-4">
            {branding.logo?.startsWith("/") && (
              <img
                src={branding.logo}
                alt={branding.name}
                className="w-10 h-10 rounded-lg"
              />
            )}
            <span className="text-2xl font-bold text-gray-900 tracking-tight">{branding.name}</span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t("resetPassword")}</h1>
          <p className="text-gray-500 mb-6">
            {t("enterYourEmailAndWellSendYouALinkToResetYourPassword")}
          </p>

          {error && (
            <Notice tone="danger" className="mb-4">
              {error}
            </Notice>
          )}

          {submitted ? (
            <Notice tone="success">
              {t("ifAnAccountExistsWithThatEmailYoullReceiveAPasswordResetLinkShortlyCheckYourInbox")}
            </Notice>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 transition"
                  placeholder={t("youexamplecom")}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl transition"
              >
                {loading ? t("sending") : t("sendResetLink")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
